/**
 * Beta waitlist (SPEC §21 decision 106), public at app.<domain>/waitlist and linked from the main website.
 * Join with an email behind Turnstile; the emailed link (double opt-in) opens `?confirm=TOKEN`, where a button
 * confirms (a POST: mail scanners that open links confirm nothing), then the page shows the person's share link and
 * how many friends confirmed through it (`?confirmed=1&ref=CODE`). `?ref=CODE` alone is a friend's share link;
 * `?error=link` is a bad or used confirmation link. The API answers the same for any email, so this page never says
 * whether an address was already on the list.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { Check, CheckCircle2, Copy, Mail, Users } from 'lucide-react';
import { BRAND } from '@vw/core/src/brand';
import { Button, Input, Spinner, copyText } from '@vw/ui';
import { api, ApiError } from '../../api';
import { cleanCode } from '../../beta';
import { useLocation } from '../../router';
import { PublicShell } from './PublicShell';
import { useTurnstile } from './turnstile';

export interface WaitlistStatus { confirmed: boolean; referrals: number; needed: number; invited: boolean; shareUrl: string }

/** Joins (or re-sends the confirmation email); `ref` is the friend's share code from the link, if any. */
export const joinWaitlist = (email: string, ref: string | null, turnstileToken: string | null) =>
  api<{ ok: true }>('/api/public/waitlist', { method: 'POST', json: { email, ref, turnstileToken } });

/** The confirm button's request: the share code back, or 404 for a bad or used link. */
export const confirmWaitlist = (t: string) =>
  api<{ ref: string }>('/api/public/waitlist/confirm', { method: 'POST', json: { t } });

/** After confirming: invited, or the friends progress and the share link. */
export function WaitlistProgress({ status }: { status: WaitlistStatus }) {
  const [copied, setCopied] = useState(false);
  if (status.invited) {
    return (
      <div className="space-y-3">
        <p className="text-sm font-semibold">You're invited: check your email for your code.</p>
        <a href="/signin" className="inline-flex rounded-xl bg-primary px-3.5 py-2 text-xs font-semibold text-primary-foreground hover:bg-primary-hover">Sign in</a>
      </div>
    );
  }
  if (!status.confirmed) return <p className="text-sm">Check your inbox to confirm your place.</p>;
  const pct = status.needed > 0 ? Math.min(100, Math.round((status.referrals / status.needed) * 100)) : 0;
  return (
    <div className="space-y-4">
      <div>
        <p className="flex items-center gap-2 text-sm font-semibold"><Users className="h-4 w-4 text-primary" />{status.referrals} of {status.needed} friends joined</p>
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-secondary" role="progressbar" aria-label="Friends joined" aria-valuemin={0} aria-valuemax={status.needed} aria-valuenow={Math.min(status.referrals, status.needed)}>
          <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
        </div>
      </div>
      <p className="text-sm">Share your link. When {status.needed} friends confirm through it, your invite arrives by email straight away.</p>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input readOnly value={status.shareUrl} onFocus={(e) => e.currentTarget.select()} className="flex-1 font-mono text-xs" aria-label="Your share link" />
        <Button className="shrink-0" onClick={async () => {
          if (await copyText(status.shareUrl, { quiet: true })) { setCopied(true); window.setTimeout(() => setCopied(false), 2000); }
        }}>{copied ? <><Check className="h-4 w-4" />Copied</> : <><Copy className="h-4 w-4" />Copy</>}</Button>
      </div>
    </div>
  );
}

export function Waitlist() {
  const { query } = useLocation();
  const ref = cleanCode(query.get('ref'));
  const token = /^[A-Za-z0-9_-]{43}$/.test(query.get('confirm') ?? '') ? query.get('confirm')! : null;
  const [confirmedRef, setConfirmedRef] = useState<string | null>(query.get('confirmed') === '1' ? ref : null);
  const [linkError, setLinkError] = useState(query.get('error') === 'link');
  return (
    <PublicShell cta={null}>
      <div className="mx-auto max-w-xl space-y-6">
        <div className="space-y-2">
          <h1 className="text-3xl font-black tracking-tight">Join the waitlist</h1>
          <p className="text-sm text-muted-foreground">{BRAND.name} is in private beta. Join the waitlist and we'll email you an invite. Already invited? <a className="font-semibold text-primary underline" href="/signin">Sign in</a>.</p>
        </div>
        {confirmedRef ? <Confirmed code={confirmedRef} />
          : token && !linkError ? <ConfirmStep token={token} onDone={setConfirmedRef} onBadLink={() => setLinkError(true)} />
            : <JoinForm refCode={ref} linkError={linkError} />}
      </div>
    </PublicShell>
  );
}

function ConfirmStep({ token, onDone, onBadLink }: { token: string; onDone: (ref: string) => void; onBadLink: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      const { ref } = await confirmWaitlist(token);
      // The token is spent: keep it out of the address bar and history.
      window.history.replaceState(null, '', `/waitlist?ref=${ref}&confirmed=1`);
      onDone(ref);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) onBadLink();
      else setError('Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="space-y-4 rounded-3xl border border-border bg-card p-6 shadow-xs">
      <p className="text-sm">Confirm your email to keep your place on the waitlist.</p>
      {error && <p role="alert" className="text-sm text-rose-600 dark:text-rose-400">{error}</p>}
      <Button size="lg" className="w-full" loading={busy} onClick={confirm}>Confirm my place</Button>
    </section>
  );
}

function Confirmed({ code }: { code: string }) {
  const [status, setStatus] = useState<WaitlistStatus | null | undefined>(undefined);
  const load = () => { setStatus(undefined); api<WaitlistStatus>(`/api/public/waitlist/status?ref=${code}`).then(setStatus, () => setStatus(null)); };
  useEffect(load, [code]);
  return (
    <section className="space-y-4 rounded-3xl border border-emerald-500/30 bg-emerald-500/5 p-6">
      <p className="flex items-center gap-2 font-bold text-emerald-700 dark:text-emerald-400"><CheckCircle2 className="h-5 w-5" />You're on the list.</p>
      {status === undefined ? <Spinner />
        : status === null ? <p className="text-sm text-muted-foreground">Couldn't load your progress. <button type="button" className="font-semibold text-primary underline" onClick={load}>Try again</button></p>
        : <WaitlistProgress status={status} />}
    </section>
  );
}

function JoinForm({ refCode, linkError }: { refCode: string | null; linkError: boolean }) {
  const { siteKey, token, blocked, box, reset } = useTurnstile('waitlist');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await joinWaitlist(email, refCode, token);
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
      reset();
    } finally {
      setBusy(false);
    }
  }

  // The same answer for every address: it never says whether one was already on the list.
  if (done) {
    return (
      <section className="space-y-2 rounded-3xl border border-emerald-500/30 bg-emerald-500/5 p-6">
        <p className="flex items-center gap-2 font-bold text-emerald-700 dark:text-emerald-400"><Mail className="h-5 w-5" />Check your inbox to confirm your place.</p>
        <p className="text-sm text-muted-foreground">Open the link in the email to confirm. Nothing after a few minutes? Check your spam folder.</p>
      </section>
    );
  }
  return (
    <>
      {linkError && <div role="alert" className="rounded-xl border border-rose-500/20 bg-rose-500/10 p-3 text-sm text-rose-600 dark:text-rose-400">That confirmation link is invalid or was already used. Enter your email again for a new one.</div>}
      {refCode && <div className="rounded-xl border border-primary/20 bg-primary/5 p-3 text-sm"><b>A friend invited you.</b> Join the waitlist to get in line.</div>}
      <form onSubmit={submit} className="space-y-5 rounded-3xl border border-border bg-card p-6 shadow-xs">
        <label className="block text-sm font-semibold">Your email
          <Input className="mt-1.5" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required maxLength={200} autoComplete="email" />
        </label>
        {siteKey && <div ref={box} />}
        {blocked && <p className="rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">The anti-spam check couldn't load (an ad blocker can stop it). Allow challenges.cloudflare.com, or email {BRAND.supportEmail} to join the waitlist.</p>}
        {siteKey === null && <p className="text-xs text-muted-foreground">If the form doesn't work, email {BRAND.supportEmail} to join the waitlist.</p>}
        {error && <p role="alert" className="text-sm text-rose-600 dark:text-rose-400">{error}</p>}
        <Button type="submit" size="lg" className="w-full" loading={busy} disabled={!!siteKey && !token}>Join the waitlist</Button>
        <p className="text-xs text-muted-foreground">We use your email only for the waitlist and your invite.</p>
      </form>
    </>
  );
}
